/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Copy, Eye, EyeOff, RefreshCw } from 'lucide-react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { SettingsSection } from '../components/settings-section'
import { updateSystemOption } from '../api'

type BoothWebhookState = {
  enabled: boolean
  secret: string
}

function generateSecret(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

export function BoothWebhookSection() {
  const { t } = useTranslation()

  const [values, setValues] = useState<BoothWebhookState>({
    enabled: false,
    secret: '',
  })
  const [original, setOriginal] = useState<BoothWebhookState>({
    enabled: false,
    secret: '',
  })
  const [showSecret, setShowSecret] = useState(false)
  const [saving, setSaving] = useState(false)

  const webhookUrl = `${window.location.origin}/api/booth/webhook`

  const isDirty =
    values.enabled !== original.enabled || values.secret !== original.secret

  useEffect(() => {
    api
      .get<{ success: boolean; data: { key: string; value: string }[] }>(
        '/api/option/'
      )
      .then((res) => {
        if (!res.data.success) return
        const opts = res.data.data ?? []
        const find = (key: string) =>
          opts.find((o) => o.key === key)?.value ?? ''

        const enabledVal = find('booth_webhook_enabled')
        const secretVal = find('booth_webhook_secret')

        const loaded: BoothWebhookState = {
          enabled: enabledVal === 'true',
          secret: secretVal,
        }
        setValues(loaded)
        setOriginal(loaded)
      })
      .catch(() => {
        // Silently ignore load errors
      })
  }, [])

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(webhookUrl).then(
      () => toast.success(t('Copied')),
      () => toast.error(t('Failed to copy'))
    )
  }

  const handleGenerate = () => {
    setValues((prev) => ({ ...prev, secret: generateSecret() }))
    setShowSecret(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const enabledRes = await updateSystemOption({
        key: 'booth_webhook_enabled',
        value: values.enabled,
      })
      if (!enabledRes.success) {
        toast.error(enabledRes.message || t('Failed to update setting'))
        return
      }

      const secretRes = await updateSystemOption({
        key: 'booth_webhook_secret',
        value: values.secret,
      })
      if (!secretRes.success) {
        toast.error(secretRes.message || t('Failed to update setting'))
        return
      }

      toast.success(t('Setting updated successfully'))
      setOriginal({ ...values })
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t('Failed to update setting')
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsSection
      title={t('BOOTH Webhook')}
      description={t(
        'Receives 注文確定 notifications from BOOTH and auto-issues redemption codes per the SKU mapping in /booth/sku'
      )}
    >
      <div className='space-y-6'>
        {/* Webhook URL */}
        <div className='space-y-2'>
          <Label>{t('Webhook URL')}</Label>
          <div className='flex items-center gap-2'>
            <Input
              readOnly
              value={webhookUrl}
              className='font-mono text-sm'
            />
            <Button
              type='button'
              variant='outline'
              size='icon'
              onClick={handleCopyUrl}
              aria-label={t('Copy webhook URL')}
            >
              <Copy className='h-4 w-4' />
            </Button>
          </div>
          <p className='text-muted-foreground text-xs'>
            {t('Register this URL as the webhook endpoint in your BOOTH shop settings.')}
          </p>
        </div>

        {/* Enabled toggle */}
        <div className='flex items-center gap-3'>
          <Switch
            id='booth-webhook-enabled'
            checked={values.enabled}
            onCheckedChange={(checked) =>
              setValues((prev) => ({ ...prev, enabled: checked }))
            }
          />
          <Label htmlFor='booth-webhook-enabled'>{t('Enabled')}</Label>
        </div>

        {/* Secret */}
        <div className='space-y-2'>
          <Label htmlFor='booth-webhook-secret'>{t('Secret')}</Label>
          <div className='flex items-center gap-2'>
            <div className='relative flex-1'>
              <Input
                id='booth-webhook-secret'
                type={showSecret ? 'text' : 'password'}
                value={values.secret}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, secret: e.target.value }))
                }
                className='pr-10 font-mono text-sm'
                placeholder={t('Enter or generate a webhook secret')}
                autoComplete='new-password'
              />
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2'
                onClick={() => setShowSecret((prev) => !prev)}
                aria-label={showSecret ? t('Hide secret') : t('Show secret')}
              >
                {showSecret ? (
                  <EyeOff className='h-4 w-4' />
                ) : (
                  <Eye className='h-4 w-4' />
                )}
              </Button>
            </div>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={handleGenerate}
            >
              <RefreshCw className='mr-1.5 h-3.5 w-3.5' />
              {t('Generate')}
            </Button>
          </div>
          <p className='text-muted-foreground text-xs'>
            {t(
              'Used to verify the authenticity of incoming BOOTH webhook requests. Set this value in your BOOTH webhook configuration.'
            )}
          </p>
        </div>

        {/* Save */}
        <div>
          <Button onClick={handleSave} disabled={!isDirty || saving} size='sm'>
            {saving ? t('Saving…') : t('Save')}
          </Button>
        </div>
      </div>
    </SettingsSection>
  )
}
